import { Dispatch, SetStateAction, useEffect } from 'react'
import { EntityType } from '../backend/types/entity.ts'
import defaultDiagramConfig from "../configs/diagram.json"
import { DiagramConfig, EntityConfig } from '../types/diagram.ts'
import { useLocalStorage } from './useLocalStorage.ts'

export const useDiagramConfig = (): {
    config: DiagramConfig,
    setConfig: Dispatch<SetStateAction<DiagramConfig>>,
    resetConfig: () => void,
    getEntityConfigOrDefault: (type: EntityType) => EntityConfig
} => {
    const [storageDiagramConfig, setLocalStorageConfig] =
        useLocalStorage<DiagramConfig>('diagramConfig', defaultDiagramConfig)

    const isStale = storageDiagramConfig.version !== defaultDiagramConfig.version

    useEffect(() => {
        if (isStale) {
            setLocalStorageConfig(() => defaultDiagramConfig)
        }
    }, [isStale, setLocalStorageConfig])

    const config: DiagramConfig = isStale ? defaultDiagramConfig : storageDiagramConfig

    const resetDiagramConfig = () => {
        setLocalStorageConfig(() => defaultDiagramConfig)
    }

    const getEntityStylePropertyOrDefault = (type: EntityType) =>
        config.entities[type] || config.defaultEntity

    const migrateConfig = (targetConfigObj: object, sourceConfigObj: object) => {
        Object.keys(sourceConfigObj).forEach((key) => {
            const configKey = key

            if (!(configKey in targetConfigObj)) {
                // @ts-expect-error types-not-match
                targetConfigObj[configKey] = sourceConfigObj[configKey]
            }
        })
    }

    // The bundled default is already complete; only the persisted copy needs backfilling.
    if (!isStale) {
        migrateConfig(config, defaultDiagramConfig)
        migrateConfig(config.entities, defaultDiagramConfig.entities)
        Object.entries(defaultDiagramConfig.entities).forEach(([key, entity]) => {
            const entityConfig = config.entities[key as EntityType]
            if (entityConfig) {
                migrateConfig(entityConfig, entity)
            }
        })
        migrateConfig(config.linkAmountText, defaultDiagramConfig.linkAmountText)
        migrateConfig(config.defaultEntity, defaultDiagramConfig.defaultEntity)
        migrateConfig(config.linkLabelText, defaultDiagramConfig.linkLabelText)
        migrateConfig(config.selected, defaultDiagramConfig.selected)
    }

    return {
        config,
        setConfig: setLocalStorageConfig,
        resetConfig: resetDiagramConfig,
        getEntityConfigOrDefault:
        getEntityStylePropertyOrDefault
    }
}
